import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Globe, Moon, Sun } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { useAdminAuth } from '../App';
import { useEffect, useState } from 'react';
import { DemoModeToggle } from './DemoModeToggle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BASE_TABS = [
  { key: 'reviews', i18nKey: 'admin.nav.reviews', path: '/admin/review-requests' },
  { key: 'experiences', i18nKey: 'admin.nav.experiences', path: '/admin/experiences' },
  { key: 'showroom', label: 'Showroom', path: '/admin/showroom' },
  { key: 'profiles', i18nKey: 'admin.nav.profiles', path: '/admin/profiles' },
  { key: 'access', i18nKey: 'admin.nav.access', path: '/admin/access' },
  { key: 'showrooms', label: 'Showrooms', path: '/admin/showrooms' },
  { key: 'expiry', label: 'Expiry', path: '/admin/link-expiry' },
  { key: 'capabilities', i18nKey: 'admin.nav.capabilities', path: '/admin/capabilities' },
];

const LANG_FLAGS = { en: 'EN', fr: 'FR', es: 'ES' };

const AdminNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language, changeLanguage, SUPPORTED_LANGUAGES } = useLanguage();
  const auth = useAdminAuth();
  const [nightMode, setNightMode] = useState(() => document.documentElement.classList.contains('dark'));

  useEffect(() => {
    if (nightMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('tapelligence-night-mode', 'true');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('tapelligence-night-mode', 'false');
    }
  }, [nightMode]);

  // Build tabs: Super Admin sees Users tab; visual-only admins see fewer write tabs
  const tabs = [...BASE_TABS];
  if (auth.role === 'super_admin') {
    tabs.push({ key: 'guests', label: 'Guests', path: '/admin/showroom-guests' });
    tabs.push({ key: 'users', i18nKey: 'admin.nav.users', path: '/admin/users' });
  }

  const activeKey = tabs.find(tab => location.pathname.startsWith(tab.path))?.key || '';

  const handleLogout = async () => {
    await fetch(`${API_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    navigate('/admin/login');
  };

  return (
    <div className="bg-white dark:bg-[#111827] border-b border-slate-200 dark:border-slate-700 sticky top-0 z-40" data-testid="admin-nav">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/tapelligence_logo_transparent.png" alt="Tapelligence" className="h-7" loading="eager" />
          <div className="h-5 w-px bg-slate-200 dark:bg-slate-600" />
          <span className="text-[10px] font-bold tracking-[0.15em] text-slate-400 uppercase">
            {auth.role === 'super_admin' ? 'SUPER ADMIN' : auth.access_type === 'full_access' ? 'ADMIN — FULL ACCESS' : 'ADMIN — VIEW'}
          </span>
        </div>
        <div className="flex items-center gap-1 text-xs">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => navigate(tab.path)}
              data-testid={`admin-nav-${tab.key}`}
              className={`px-3 py-1.5 rounded-lg transition-colors ${
                activeKey === tab.key
                  ? 'text-[#0077FF] bg-blue-50 font-medium'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
              }`}
            >
              {tab.label || t(tab.i18nKey) || tab.key}
            </button>
          ))}
          <div className="h-4 w-px bg-slate-200 dark:bg-slate-600 mx-1" />
          <button
            onClick={() => setNightMode(m => !m)}
            data-testid="night-mode-toggle"
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-700 transition-colors"
            title={nightMode ? 'Switch to Light Mode' : 'Switch to Night Mode'}
          >
            {nightMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <div className="h-4 w-px bg-slate-200 dark:bg-slate-600 mx-1" />
          <div className="flex items-center gap-0.5 mr-1" data-testid="admin-lang-switcher">
            <Globe className="w-3.5 h-3.5 text-slate-400 mr-0.5" />
            {SUPPORTED_LANGUAGES.map(lang => (
              <button
                key={lang}
                onClick={() => changeLanguage(lang)}
                data-testid={`admin-lang-${lang}`}
                className={`px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wider transition-colors ${
                  language === lang
                    ? 'bg-[#0077FF] text-white'
                    : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                }`}
              >
                {LANG_FLAGS[lang]}
              </button>
            ))}
          </div>
          <div className="h-4 w-px bg-slate-200 dark:bg-slate-600 mx-1" />
          {auth.role === 'super_admin' && (
            <>
              <DemoModeToggle />
              <div className="h-4 w-px bg-slate-200 dark:bg-slate-600 mx-1" />
            </>
          )}
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            {t('admin.nav.logout')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminNav;
